import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { X, Users } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import { DataTable, DataTableEmpty } from '../components/ui/DataTable';
import './TeamManagementPage.css';

/**
 * Team Management page - User management (admin only).
 * Manage users, roles, and account status.
 */
export const TeamManagementPage = () => {
  const { user } = useEntitlements();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'staff',
    client: '',
    can_view_invoices: true,
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      // Fetch only staff users (exclude clients)
      const response = await api.get('/v1/users/?role=staff');
      const allUsers = response.data.results || response.data;
      // Filter to show only staff and admin (not clients)
      const staffUsers = allUsers.filter(u => u.role === 'staff' || u.role === 'admin');
      setUsers(staffUsers);
      console.log('Fetched users:', staffUsers); // Debug log
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || 'Failed to fetch users';
      setError(errorMessage);
      console.error('Failed to fetch users:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Add is_active and status to formData
      const userData = {
        ...formData,
        is_active: true,
        status: 'active',
        can_view_invoices: formData.role === 'admin' ? true : formData.can_view_invoices,
      };
      await api.post('/v1/users/', userData);
      setShowCreateModal(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'staff',
        client: '',
        can_view_invoices: true,
      });
      setError(null); // Clear any previous errors
      fetchUsers();
    } catch (err) {
      // Show detailed error message from backend
      const errorData = err.response?.data;
      let errorMessage = 'Failed to create user';
      
      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }
      
      setError(errorMessage);
      console.error('User creation error:', err.response?.data || err);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    if (!window.confirm(`Change user role to ${newRole}?`)) {
      return;
    }
    try {
      await api.post(`/v1/users/${userId}/change_role/`, { role: newRole });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) {
      return;
    }
    try {
      await api.post(`/v1/users/${userId}/suspend/`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.post(`/v1/users/${userId}/activate/`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate user');
    }
  };

  const handleToggleCanViewInvoices = async (teamUser) => {
    const newValue = !teamUser.can_view_invoices;
    try {
      await api.patch(`/v1/users/${teamUser.id}/`, {
        can_view_invoices: newValue,
      });
      fetchUsers();
      if (viewingUser?.id === teamUser.id) {
        setViewingUser({ ...viewingUser, can_view_invoices: newValue });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to update permission');
    }
  };

  const handleSendPasswordReset = async (teamUser) => {
    if (!window.confirm(`Send password reset to ${teamUser.first_name} ${teamUser.last_name}? A temporary password will be emailed to ${teamUser.email}.`)) {
      return;
    }
    try {
      const response = await api.post(`/v1/users/${teamUser.id}/send_password_reset/`);
      if (response.data.email_sent) {
        alert(`Password reset email sent successfully to ${teamUser.email}`);
      } else {
        alert(`Password was reset but email failed to send. Please contact the user manually.`);
      }
      fetchUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to send password reset';
      setError(errorMsg);
    }
  };

  if (!isAdmin) {
    return (
      <div className="team-management-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You must be an administrator to access team management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="team-loading">Loading team members...</div>;
  }

  return (
    <div className="team-management-page">
      <PageHeader
        icon={Users}
        title="Team Management"
        subtitle="Manage staff access, roles, and account state."
        primaryAction={(
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Add User
          </button>
        )}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="team-filters">
        <select>
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="staff">Staff</option>
        </select>
        <select>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <DataTable className="team-list">
        {users.length === 0 ? (
          <DataTableEmpty>
            <div className="team-empty">
              <p>No users found.</p>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Add Your First User
              </button>
            </div>
          </DataTableEmpty>
        ) : (
          <table className="team-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>View Invoices</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((teamUser) => (
                <tr key={teamUser.id}>
                  <td data-label="Name">
                    <button
                      type="button"
                      className="team-member-name-link"
                      onClick={() => setViewingUser(teamUser)}
                    >
                      {teamUser.first_name} {teamUser.last_name}
                    </button>
                  </td>
                  <td data-label="Email">{teamUser.email}</td>
                  <td data-label="Role">
                    <StatusBadge
                      className="role-badge"
                      label={teamUser.role}
                      variant={teamUser.role === 'admin' ? 'accent' : 'neutral'}
                    />
                  </td>
                  <td data-label="Status">
                    <StatusBadge
                      label={teamUser.status || 'active'}
                      variant={(teamUser.status || 'active') === 'active' ? 'success' : 'danger'}
                    />
                  </td>
                  <td data-label="View Invoices">
                    {teamUser.role === 'admin' ? (
                      <span className="permission-badge">Yes</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={teamUser.can_view_invoices !== false}
                        onChange={() => handleToggleCanViewInvoices(teamUser)}
                        title="Can view invoices"
                      />
                    )}
                  </td>
                  <td data-label="Last Login">
                    {teamUser.last_login
                      ? new Date(teamUser.last_login).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td data-label="Actions">
                    <div className="team-actions" onClick={(e) => e.stopPropagation()}>
                      {teamUser.role !== 'admin' && (
                        <select
                          value={teamUser.role}
                          onChange={(e) => handleChangeRole(teamUser.id, e.target.value)}
                          className="role-select"
                        >
                          <option value="staff">Staff</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleSendPasswordReset(teamUser)}
                        title="Send Password Reset"
                      >
                        Reset Password
                      </button>
                      {teamUser.status === 'active' ? (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleSuspend(teamUser.id)}
                        >
                          Suspend
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleActivate(teamUser.id)}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTable>

      {/* Team Member Detail Side Panel */}
      {viewingUser && (
        <div className="detail-panel-overlay" onClick={() => setViewingUser(null)}>
          <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="detail-panel-header">
              <h2>{viewingUser.first_name} {viewingUser.last_name}</h2>
              <button
                className="detail-panel-close"
                onClick={() => setViewingUser(null)}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="detail-panel-body">
              <section className="detail-section">
                <div className="detail-grid">
                  <div className="detail-field detail-field-full">
                    <label>Name</label>
                    <span>{viewingUser.first_name} {viewingUser.last_name}</span>
                  </div>
                  <div className="detail-field detail-field-full">
                    <label>Email</label>
                    <span>{viewingUser.email}</span>
                  </div>
                  <div className="detail-field">
                    <label>Role</label>
                    <StatusBadge
                      className="role-badge"
                      label={viewingUser.role}
                      variant={viewingUser.role === 'admin' ? 'accent' : 'neutral'}
                    />
                  </div>
                  <div className="detail-field">
                    <label>Status</label>
                    <StatusBadge
                      label={viewingUser.status || 'active'}
                      variant={(viewingUser.status || 'active') === 'active' ? 'success' : 'danger'}
                    />
                  </div>
                  <div className="detail-field">
                    <label>Last Login</label>
                    <span>
                      {viewingUser.last_login
                        ? new Date(viewingUser.last_login).toLocaleDateString()
                        : 'Never'}
                    </span>
                  </div>
                  <div className="detail-field detail-field-full">
                    <label>Can view invoices</label>
                    {viewingUser.role === 'admin' ? (
                      <span>Yes (admin)</span>
                    ) : (
                      <label>
                        <input
                          type="checkbox"
                          checked={viewingUser.can_view_invoices !== false}
                          onChange={() => {
                            handleToggleCanViewInvoices(viewingUser);
                            setViewingUser({ ...viewingUser, can_view_invoices: !viewingUser.can_view_invoices });
                          }}
                        />
                        {' '}{viewingUser.can_view_invoices !== false ? 'Yes' : 'No'}
                      </label>
                    )}
                  </div>
                </div>
                <div className="detail-panel-actions">
                  {viewingUser.role !== 'admin' && (
                    <select
                      value={viewingUser.role}
                      onChange={(e) => {
                        handleChangeRole(viewingUser.id, e.target.value);
                      }}
                      className="role-select"
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleSendPasswordReset(viewingUser)}
                  >
                    Reset Password
                  </button>
                  {viewingUser.status === 'active' ? (
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        handleSuspend(viewingUser.id);
                        setViewingUser(null);
                      }}
                    >
                      Suspend
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        handleActivate(viewingUser.id);
                        setViewingUser(null);
                      }}
                    >
                      Activate
                    </button>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New User</h2>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {formData.role === 'staff' && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.can_view_invoices !== false}
                      onChange={(e) => setFormData({ ...formData, can_view_invoices: e.target.checked })}
                    />
                    {' '}Can view invoices
                  </label>
                </div>
              )}
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

